/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Listings in UI v2 (design-reference "Real Estate · Listings"): a card per
 * listing with its badge, photo count, price, address, beds/baths/cars, agent
 * and enquiries.
 *
 * Same request as the classic list (GET /re/listings, server-paged), which
 * now carries the unit's facts and the counts (crud.brokerage._listing_extras).
 * "New listing" opens the classic list's create form, in the tenant's country.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { Bath, BedDouble, Car, House, Plus } from "lucide-react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "components/v2/PageHeader";
import SegmentedControl from "components/v2/SegmentedControl";
import { FilterBar, FilterSearch, FilterSelect } from "components/v2/filters";
import { EntityFormDialog } from "components/EntityFramework";
import { usePermission } from "auth/usePermission";
import { v2 as t } from "styles/tokens/v2";
import { formatNumber, useReList } from "../screens/api";
import { getReEntityConfig } from "../reEntityConfig";
import { localiseConfig } from "../reEntityVariant";
import useReVariant from "../useReVariant";
import { isAustralia } from "../variant";
import { Avatar, Pill, RE_ACCENT, coverUrl, listingBadge, listingPrice } from "./parts";

const STATUS_FILTERS = [
  { value: "ACTIVE,UNDER_OFFER", label: "On the market" },
  { value: "ACTIVE", label: "Active" },
  { value: "UNDER_OFFER", label: "Under offer" },
  { value: "DRAFT", label: "Off market" },
  { value: "CLOSED", label: "Sold or leased" },
  { value: "", label: "All" },
];

function Fact({ icon: Icon, value, unit }) {
  if (value === null || value === undefined) return null;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, color: t.text2, fontSize: 13.5 }}>
      <Icon size={16} aria-hidden />
      {`${value} ${unit}`}
    </Box>
  );
}

export function ListingCard({ listing, onOpen }) {
  const badge = listingBadge(listing);
  const cover = coverUrl(listing);
  const agent = listing.assigned_to_name || listing.broker_agent_name;
  const place = [listing.unit_number && listing.project_name ? `${listing.unit_number}, ${listing.project_name}` : listing.project_name, listing.address]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      component="article"
      sx={{
        bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "10px", overflow: "hidden",
        display: "flex", flexDirection: "column", transition: "border-color 120ms, box-shadow 120ms",
        "&:hover": { borderColor: t.border2, boxShadow: t.shadowMenu },
      }}
    >
      <Box
        sx={{
          position: "relative", height: 168, bgcolor: "#EEF1F6",
          backgroundImage: cover ? `url(${cover})` : "none", backgroundSize: "cover", backgroundPosition: "center",
          display: "flex", alignItems: "center", justifyContent: "center", color: "#8A97AB",
        }}
      >
        {!cover && <House size={40} strokeWidth={1.5} aria-hidden />}
        <Box sx={{ position: "absolute", top: 12, left: 12 }}><Pill tone={badge.tone}>{badge.label}</Pill></Box>
        {listing.photo_count > 0 && (
          <Box sx={{ position: "absolute", right: 12, bottom: 12, px: 1, py: 0.25, borderRadius: "6px", bgcolor: "rgba(14,35,72,0.78)", color: "#FFFFFF", fontSize: 12, fontWeight: 600 }}>
            {`${listing.photo_count} photo${listing.photo_count === 1 ? "" : "s"}`}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: t.text }}>{listingPrice(listing)}</Typography>
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          sx={{ p: 0, border: 0, bgcolor: "transparent", textAlign: "left", cursor: "pointer", font: "inherit", fontSize: 15, fontWeight: 600, color: t.link, "&:hover": { textDecoration: "underline" } }}
        >
          {listing.title}
        </Box>
        {place && <Typography sx={{ fontSize: 13.5, color: t.text3 }}>{place}</Typography>}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
          <Fact icon={BedDouble} value={listing.bedrooms === 0 ? "Studio" : listing.bedrooms} unit={listing.bedrooms === 0 ? "" : "bed"} />
          <Fact icon={Bath} value={listing.bathrooms} unit="bath" />
          <Fact icon={Car} value={listing.parking_spaces} unit="car" />
        </Box>
        <Box sx={{ mt: "auto", pt: 1.5, borderTop: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 1 }}>
          {agent ? <Avatar name={agent} /> : null}
          <Typography sx={{ fontSize: 13.5, color: t.text2, flex: 1, minWidth: 0 }} noWrap>{agent || "No agent"}</Typography>
          <Typography sx={{ fontSize: 13, color: t.text3 }}>{`${formatNumber(listing.enquiry_count || 0)} enquiries`}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function ListingsV2() {
  const navigate = useNavigate();
  const pack = useReVariant();
  const { canCreate } = usePermission("re_listings");
  const [purpose, setPurpose] = useState("");
  const [status, setStatus] = useState("ACTIVE,UNDER_OFFER");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const list = useReList("/re/listings", { purpose: purpose || null, status: status || null, search: search || null }, { pageSize: 24 });
  const active = useReList("/re/listings", { status: "ACTIVE" }, { pageSize: 1 });
  const offer = useReList("/re/listings", { status: "UNDER_OFFER" }, { pageSize: 1 });
  const config = useMemo(() => localiseConfig(getReEntityConfig("re-listings"), pack), [pack]);
  const pages = Math.max(1, Math.ceil((list.total || 0) / list.pageSize));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6 }}>
        <PageHeader
          title="Listings"
          summary={`${formatNumber(active.total || 0)} active listings · ${formatNumber(offer.total || 0)} under offer`}
          actions={canCreate ? (
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setCreating(true)} sx={{ color: "#FFFFFF !important" }}>
              New listing
            </Button>
          ) : null}
        />

        <FilterBar count={`${formatNumber(list.total || 0)} shown`} sx={{ mb: 2.5 }}>
          <SegmentedControl
            value={purpose}
            onChange={setPurpose}
            options={[
              { value: "", label: "All" },
              { value: "SALE", label: "For sale" },
              { value: "RENT", label: isAustralia() ? "For lease" : "For rent" },
            ]}
            label="Purpose"
          />
          <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_FILTERS} minWidth={170} />
          <FilterSearch placeholder="Search listings" value={search} onChange={setSearch} width={240} />
        </FilterBar>

        {list.error && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={list.reload}>Retry</Button>}>Listings could not be loaded.</Alert>}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" }, gap: 2 }}>
          {list.loading && !list.items.length
            ? [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={340} />)
            : list.items.map((l) => <ListingCard key={l.id} listing={l} onOpen={() => navigate(`/real-estate/listings/${l.id}`)} />)}
        </Box>

        {!list.loading && !list.items.length && !list.error && (
          <Box sx={{ textAlign: "center", py: 6, color: t.text3 }}>
            <House size={32} color={RE_ACCENT.ink} aria-hidden />
            <Typography sx={{ mt: 1, fontSize: 15, color: t.text2 }}>No listings match.</Typography>
          </Box>
        )}

        {pages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, mt: 3 }}>
            <Button variant="outlined" disabled={list.page <= 1} onClick={() => list.setPage(list.page - 1)}>Previous</Button>
            <Typography sx={{ fontSize: 13.5, color: t.text2 }}>{`Page ${list.page} of ${pages}`}</Typography>
            <Button variant="outlined" disabled={list.page >= pages} onClick={() => list.setPage(list.page + 1)}>Next</Button>
          </Box>
        )}
      </Box>

      {canCreate && (
        <EntityFormDialog
          open={creating}
          onClose={() => setCreating(false)}
          title="New listing"
          apiPath="/re/listings"
          fields={config.createForm.fields}
          onCreated={(row) => {
            setCreating(false);
            if (row?.id) navigate(`/real-estate/listings/${row.id}`);
            else list.reload();
          }}
        />
      )}
    </DashboardLayout>
  );
}
